import { AxiosResponse } from '../../axios';
import * as uploads from '../../uploads';
import { CreateMultipleRequestResp, CreateMultipleRequests, UserEventRequest } from '../revisions';
import { Marker, Point, StoredMarker } from './marker';

export declare interface RevisionOpts {
    proofID: string;
    quoteHash: string;
    element: HTMLElement;
    readonly?: boolean;
    uploader?: uploads.Uploader;
}

declare interface storage {
    markers: StoredMarker[];
}

export class Revision {
    Markers: Marker[] = [];
    CommittedMarkerNumbers: number[] = [];

    GeneralNotes?: string;
    Uploader?: uploads.Uploader;

    private proofID: string;
    private quoteHash: string;

    LocalStorageKey: string;

    Element: HTMLElement;
    Proof: HTMLImageElement;

    private readonly: boolean;

    OnMarkerAdd?: (m: Marker) => void;
    OnMarkerRenumber?: (n: number) => void;

    constructor(options: RevisionOpts) {
        this.proofID = options.proofID;
        this.quoteHash = options.quoteHash;
        this.LocalStorageKey = `${this.proofID}-markers`;
        this.Element = options.element;
        this.Uploader = options.uploader;
        this.readonly = options.readonly || false;

        this.Element.style.position = 'relative';
        this.Proof = this.Element.querySelector('img') as HTMLImageElement;

        if (!this.readonly) {
            this.Proof.addEventListener('mouseenter', () => this.Proof.style.cursor = 'crosshair');
            this.Proof.addEventListener('click', e => {
                e.preventDefault();
                const p = this.getRelativeMousePosition(e);
                this.AddMarker({
                    x: p.x * (this.Proof.naturalWidth / this.Proof.clientWidth),
                    y: p.y * (this.Proof.naturalHeight / this.Proof.clientHeight),
                });
            });
        }
    }

    private checkMarkerRange(n: number): boolean {
        return this.Markers.length >= n;
    }

    AddMarker(p: Point, text?: string, committed: boolean = false) {
        const m = new Marker({
            markerNumber: this.Markers.length + 1,
            revision: this,
            committed: committed || this.readonly,
        });
        this.Markers.push(m);

        if (text) {
            m.SetNotes(text);
        }
        m.SetPoint(p);
        this.updateLocalStorage();

        if (committed || this.readonly) {
            this.CommittedMarkerNumbers.push(this.GetMarkerNumber(m));
        }

        if (this.OnMarkerAdd && !committed) {
            this.OnMarkerAdd(m);
        }
    }

    RemoveMarker(n: number) {
        if (!this.checkMarkerRange(n)) {
            return;
        }

        this.Markers[n - 1].Remove();

        this.Markers.splice(n - 1, 1);
        this.reorderMarkers(n);
        this.updateLocalStorage();
    }

    RemoveAllMarkers() {
        let i = 0;
        for (const m of this.Markers) {
            if (m.Committed) {
                i++;
                continue;
            }
            m.Remove();
        }

        this.Markers.length = i;
    }

    MoveMarker(n: number, p: Point) {
        if (!this.checkMarkerRange(n)) {
            return;
        }

        const marker = this.Markers[n - 1];
        marker.SetPoint(p);
        this.updateLocalStorage();
    }

    GetMarker(n: number): StoredMarker | undefined {
        if (!this.checkMarkerRange(n)) {
            return;
        }
        return this.Markers[n - 1].GetMarker();
    }

    GetMarkerNumber(m: Marker): number {
        return this.Markers.indexOf(m) + 1;
    }

    private reorderMarkers(n: number) {
        for (const m of this.Markers) {
            m.Renumber(this.GetMarkerNumber(m));
        }

        if (this.OnMarkerRenumber) {
            this.OnMarkerRenumber(n);
        }
    }

    SetText(n: number, text: string) {
        if (!this.checkMarkerRange(n)) {
            return;
        }
        const marker = this.Markers[n - 1];

        marker.SetNotes(text);
        this.updateLocalStorage();
    }

    private updateLocalStorage() {
        const s: storage = { markers: [] };

        for (const m of this.Markers) {
            if (m.Committed) {
                continue;
            }
            s.markers.push(m.GetMarker());
        }

        window.localStorage.setItem(this.LocalStorageKey, JSON.stringify(s));
    }

    LatestMarkerNumber() {
        return this.Markers.length;
    }

    LoadLocalStorage() {
        const m = window.localStorage.getItem(this.LocalStorageKey);
        if (!m) {
            return;
        }

        const s: storage = JSON.parse(m) as storage;
        for (const marker of s.markers) {
            this.AddMarker(marker.position, marker.notes);
        }
    }

    ClearLocalStorage() {
        window.localStorage.removeItem(this.LocalStorageKey);
    }

    Submit(): Promise<AxiosResponse<CreateMultipleRequestResp>> {
        const userEvents: UserEventRequest[] = [];
        for (const marker of this.Markers) {
            if (!marker.Committed) {
                const num = this.GetMarkerNumber(marker);
                userEvents.push({
                    quoteProofID: this.proofID,
                    notes: `${num}. ${marker.GetNotes()}`,
                    uploadIDs: [],
                    point: marker.GetPoint(),
                    pointNumber: num,
                });
            }
        }

        if (!this.Uploader) {
            throw new Error('no uploader');
        }
        const uploadIDs = this.Uploader.GetUploadIDs();
        if (this.GeneralNotes || uploadIDs.length) {
            userEvents.push({
                quoteProofID: this.proofID,
                notes: this.GeneralNotes || '',
                uploadIDs,
            });
        }

        return CreateMultipleRequests(this.quoteHash, userEvents);
    }

    private getRelativeMousePosition(e: MouseEvent): Point {
        const ele = e.currentTarget as HTMLElement;
        const r = ele.getBoundingClientRect();
        return {
            x: e.clientX - r.left,
            y: e.clientY - r.top,
        };
    }

    FocusMarker(n: number) {
        for (const m of this.Markers) {
            if (this.GetMarkerNumber(m) === n) {
                m.Focus();
            } else {
                m.RemoveFocus();
            }
        }
    }

    RemoveFocus() {
        for (const m of this.Markers) {
            m.Focus();
        }
    }

    HideMarker(n: number) {
        if (!this.checkMarkerRange(n)) {
            return;
        }
        this.Markers[n - 1].HideMarker();
    }

    ShowMarker(n: number) {
        if (!this.checkMarkerRange(n)) {
            return;
        }
        this.Markers[n - 1].Focus();
    }

    SetCommitted(n: number, committed: boolean) {
        if (!this.checkMarkerRange(n)) {
            return;
        }

        this.Markers[n - 1].Committed = committed;

        if (committed) {
            this.CommittedMarkerNumbers.push(n);
        } else {
            const index = this.CommittedMarkerNumbers.indexOf(n);
            if (index > -1) {
                this.CommittedMarkerNumbers.splice(index, 1);
            }
        }
    }
}